/**
 * useListQuery<T> — React hook for server-side paginated list queries with URL sync.
 *
 * Syncs filter/sort/search state to the URL query string and handles debounced search,
 * pagination, and abort-on-change for in-flight requests.
 *
 * Response envelope expected:
 *   { items: T[], page: number, page_size: number, total: number, sort: string | null, order: 'asc' | 'desc' | null, applied_filters: Array<{ key: string; label: string; value: string }> }
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import client from '@/api/client';

export interface UseListQueryOptions {
  endpoint: string;
  initialParams?: Record<string, unknown>;
  pageSize?: number;
}

export interface ListQueryResponse<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  sort: string | null;
  order: 'asc' | 'desc' | null;
  applied_filters: Array<{ key: string; label: string; value: string }>;
}

export interface UseListQueryResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  sort: string | null;
  order: 'asc' | 'desc' | null;
  filters: Record<string, unknown>;
  search: string;
  loading: boolean;
  error: Error | null;
  setPage: (p: number) => void;
  setSort: (key: string, order?: 'asc' | 'desc') => void;
  setFilter: (key: string, value: unknown) => void;
  setSearch: (q: string) => void;
  clearAll: () => void;
  refetch: () => void;
  appliedFilters: Array<{ key: string; label: string; value: string }>;
}

export function useListQuery<T>({
  endpoint,
  initialParams = {},
  pageSize = 50,
}: UseListQueryOptions): UseListQueryResult<T> {
  // ============================================================================
  // State
  // ============================================================================
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc' | null>(null);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<
    Array<{ key: string; label: string; value: string }>
  >([]);

  // ============================================================================
  // Refs for debouncing and abort control
  // ============================================================================
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Callers typically pass initialParams as an inline object literal, which gets
  // a new identity every render. Hold it in a ref so it doesn't destabilize the
  // memoized fetchData and kick off an infinite fetch/abort loop.
  const initialParamsRef = useRef(initialParams);
  initialParamsRef.current = initialParams;

  // ============================================================================
  // Hydrate from URL on mount
  // ============================================================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Extract page
    const p = Number(params.get('page')) || 1;
    setPage(p);

    // Extract sort/order
    const s = params.get('sort');
    const o = params.get('order') as 'asc' | 'desc' | null;
    if (s) setSort(s);
    if (o && (o === 'asc' || o === 'desc')) setOrder(o);

    // Extract search
    const q = params.get('q') || '';
    setSearch(q);

    // Extract filters (any param that is not page, sort, order, q)
    const extractedFilters: Record<string, unknown> = {};
    params.forEach((value, key) => {
      if (!['page', 'sort', 'order', 'q'].includes(key)) {
        // Handle comma-separated values as arrays
        if (value.includes(',')) {
          extractedFilters[key] = value.split(',');
        } else {
          extractedFilters[key] = value;
        }
      }
    });
    setFilters(extractedFilters);
  }, []);

  // ============================================================================
  // Build query string from current state
  // ============================================================================
  const buildQueryString = useCallback(
    (p: number, s: string | null, o: 'asc' | 'desc' | null, q: string, f: Record<string, unknown>) => {
      const params = new URLSearchParams();
      if (p > 1) params.set('page', String(p));
      if (s) params.set('sort', s);
      if (o) params.set('order', o);
      if (q) params.set('q', q);

      // Add filter params
      Object.entries(f).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });

      return params.toString();
    },
    []
  );

  // ============================================================================
  // Build API request params from state
  // ============================================================================
  const buildApiParams = useCallback(
    (p: number, s: string | null, o: 'asc' | 'desc' | null, q: string, f: Record<string, unknown>) => {
      const apiParams: Record<string, unknown> = {
        ...initialParamsRef.current,
        page: p,
        page_size: pageSize,
      };

      if (s) apiParams.sort = s;
      if (o) apiParams.order = o;
      if (q) apiParams.q = q;

      // Merge filter params
      Object.entries(f).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          apiParams[`${key}__in`] = value.join(',');
        } else if (value !== null && value !== undefined && value !== '') {
          apiParams[key] = value;
        }
      });

      return apiParams;
    },
    [pageSize]
  );

  // ============================================================================
  // Fetch data
  // ============================================================================
  const fetchData = useCallback(
    async (p: number, s: string | null, o: 'asc' | 'desc' | null, q: string, f: Record<string, unknown>) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const ac = new AbortController();
      abortControllerRef.current = ac;

      setLoading(true);
      setError(null);

      try {
        const apiParams = buildApiParams(p, s, o, q, f);

        const response = await client.get<ListQueryResponse<T>>(endpoint, {
          params: apiParams,
          signal: ac.signal,
        });

        // Only update if this request wasn't aborted
        if (!ac.signal.aborted) {
          setItems(response.data.items);
          setTotal(response.data.total);
          setPage(response.data.page);
          setSort(response.data.sort ?? null);
          setOrder(response.data.order ?? null);
          setAppliedFilters(response.data.applied_filters ?? []);
          setLoading(false);
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          if (err instanceof Error) {
            setError(err);
          } else {
            setError(new Error('Failed to fetch data'));
          }
          setLoading(false);
        }
      }
    },
    [endpoint, buildApiParams]
  );

  // ============================================================================
  // Sync state changes to URL and fetch
  // ============================================================================
  useEffect(() => {
    const qs = buildQueryString(page, sort, order, search, filters);
    // Keep the app's `#view` hash so the entry still identifies its screen, and
    // preserve the existing history.state (App stores {view,...} there for its
    // popstate handler). Passing `null` here would wipe that state, so pressing
    // Back onto a list view would land on the fallback (map) instead of it.
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);

    // Clear any pending search debounce before fetching
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    // Fetch immediately
    fetchData(page, sort, order, search, filters);
  }, [page, sort, order, search, filters, buildQueryString, fetchData]);

  // ============================================================================
  // State setters
  // ============================================================================
  const handleSetPage = useCallback((p: number) => {
    setPage(p);
  }, []);

  const handleSetSort = useCallback((key: string, o?: 'asc' | 'desc') => {
    setSort(key);
    setOrder(o ?? null);
    setPage(1); // Reset to first page on sort change
  }, []);

  const handleSetFilter = useCallback((key: string, value: unknown) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === null || value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPage(1); // Reset to first page on filter change
  }, []);

  const handleSetSearch = useCallback((q: string) => {
    setSearch(q);
    setPage(1); // Reset to first page on search change

    // Debounce the search fetch by 300ms
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      // Search will trigger via the useEffect dependency
    }, 300);
  }, []);

  const handleClearAll = useCallback(() => {
    setPage(1);
    setSort(null);
    setOrder(null);
    setFilters({});
    setSearch('');

    // Push to history (not replace) on clear all so back button works.
    // Preserve the app's {view,...} history.state + `#view` hash so a later
    // Back press still restores the correct screen.
    const url = `${window.location.pathname}${window.location.hash}`;
    window.history.pushState(window.history.state, '', url);
  }, []);

  const handleRefetch = useCallback(() => {
    fetchData(page, sort, order, search, filters);
  }, [page, sort, order, search, filters, fetchData]);

  // ============================================================================
  // Cleanup on unmount
  // ============================================================================
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    items,
    total,
    page,
    pageSize,
    sort,
    order,
    filters,
    search,
    loading,
    error,
    setPage: handleSetPage,
    setSort: handleSetSort,
    setFilter: handleSetFilter,
    setSearch: handleSetSearch,
    clearAll: handleClearAll,
    refetch: handleRefetch,
    appliedFilters,
  };
}
